import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiUser, FiEye, FiBell, FiBook, FiVolume2, FiLock, FiSave } = FiIcons;

const PersonalizationTab = () => {
    const { user, updateUser } = useAuth();
    const { config } = useConfig();
    const [formData, setFormData] = useState({
        name: user.name || '',
        email: user.email || '',
        language: user.preferences?.language || 'english',
        translation: user.preferences?.translation || 'english',
        theme: user.preferences?.theme || 'light',
        arabicFont: user.preferences?.arabicFont || 'amiri',
        fontSize: user.preferences?.fontSize || 'medium',
        notifications: user.preferences?.notifications || {
            dailyReminders: true,
            prayerNotifications: true,
            emailUpdates: false
        },
        reading: user.preferences?.reading || {
            showTransliteration: true,
            autoPlayAudio: false
        },
        audio: user.preferences?.audio || {
            defaultReciter: 'mishaari_raashid_al_3afaasee',
            playbackSpeed: 1.0
        },
        privacy: user.preferences?.privacy || {
            profileVisibility: 'public'
        }
    });

    const handleInputChange = (section, field, value) => {
        if (section === 'basic') {
            setFormData(prev => ({ ...prev, [field]: value }));
        } else {
            setFormData(prev => ({
                ...prev,
                [section]: { ...prev[section], [field]: value }
            }));
        }
    };

    const handleSave = () => {
        updateUser({
            name: formData.name,
            email: formData.email,
            preferences: {
                language: formData.language,
                translation: formData.translation,
                theme: formData.theme,
                arabicFont: formData.arabicFont,
                fontSize: formData.fontSize,
                notifications: formData.notifications,
                reading: formData.reading,
                audio: formData.audio,
                privacy: formData.privacy
            }
        });

        toast.success('Settings saved successfully!');
    };

    const sections = [
        {
            id: 'general',
            title: 'General Settings',
            icon: FiUser,
            fields: [
                {
                    type: 'text',
                    label: 'Full Name',
                    value: formData.name,
                    onChange: (value) => handleInputChange('basic', 'name', value)
                },
                {
                    type: 'email',
                    label: 'Email Address',
                    value: formData.email,
                    // readOnly: true,
                    onChange: (value) => handleInputChange('basic', 'email', value)
                },
                {
                    type: 'text',
                    label: 'Joined',
                    value: new Date(user.joinedDate || Date.now()).toLocaleDateString(),
                    readOnly: true
                },
                {
                    type: 'text',
                    label: 'Last Login',
                    value: new Date(user.lastLogin || Date.now()).toLocaleString(),
                    readOnly: true
                },
                {
                    type: 'select',
                    label: 'Interface Language',
                    value: formData.language,
                    options: [
                        { value: 'english', label: 'English' },
                        { value: 'urdu', label: 'اردو (Urdu)' },
                        { value: 'arabic', label: 'العربية (Arabic)' },
                        { value: 'french', label: 'Français (French)' },
                        { value: 'spanish', label: 'Español (Spanish)' },
                        { value: 'indonesian', label: 'Bahasa Indonesia' },
                        { value: 'turkish', label: 'Türkçe (Turkish)' },
                        { value: 'russian', label: 'Русский (Russian)' },
                        { value: 'chinese', label: '中文 (Chinese)' }
                    ],
                    onChange: (value) => handleInputChange('basic', 'language', value)
                },
                {
                    type: 'select',
                    label: 'Default Translation',
                    value: formData.translation,
                    options: [
                        { value: 'arabic', label: 'Arabic' },
                        { value: 'bangla', label: 'Bangla' },
                        { value: 'chinese', label: 'Chinese (Ma Jian)' },
                        { value: 'english', label: 'English' },
                        { value: 'farsi', label: 'Farsi (Persian)' },
                        { value: 'french', label: 'French (Hamidullah)' },
                        { value: 'hindi', label: 'Hindi' },
                        { value: 'indonesian', label: 'Indonesian (Bahasa)' },
                        { value: 'japanese', label: 'Japanese' },
                        { value: 'russian', label: 'Russian (Kuliev)' },
                        { value: 'spanish', label: 'Spanish (Cortes)' },
                        { value: 'turkish', label: 'Turkish (Diyanet)' },
                        { value: 'urdu', label: 'Urdu (Jalandhry)' }
                    ],
                    onChange: (value) => handleInputChange('basic', 'translation', value)
                }
            ]
        },
        {
            id: 'appearance',
            title: 'Appearance',
            icon: FiEye,
            fields: [
                {
                    type: 'radio',
                    label: 'Theme',
                    value: formData.theme,
                    options: [
                        { value: 'light', label: 'Light Theme' },
                        { value: 'dark', label: 'Dark Theme' }
                    ],
                    onChange: (value) => handleInputChange('basic', 'theme', value)
                },
                {
                    type: 'select',
                    label: 'Arabic Font',
                    value: formData.arabicFont,
                    options: [
                        { value: 'amiri', label: 'Amiri (Classic)' },
                        { value: 'noto-sans-arabic', label: 'Noto Sans Arabic (Modern)' },
                        { value: 'scheherazade', label: 'Scheherazade New (Traditional)' },
                        { value: 'uthmanic', label: 'Uthmanic Script' }
                    ],
                    onChange: (value) => handleInputChange('basic', 'arabicFont', value)
                },
                {
                    type: 'select',
                    label: 'Font Size',
                    value: formData.fontSize,
                    options: [
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                        { value: 'xlarge', label: 'Extra Large' }
                    ],
                    onChange: (value) => handleInputChange('basic', 'fontSize', value)
                }
            ]
        },
        {
            id: 'notifications',
            title: 'Notifications',
            icon: FiBell,
            fields: [
                {
                    type: 'toggle',
                    label: 'Daily Reminders',
                    value: formData.notifications.dailyReminders,
                    onChange: (value) => handleInputChange('notifications', 'dailyReminders', value)
                },
                {
                    type: 'toggle',
                    label: 'Prayer Notifications',
                    value: formData.notifications.prayerNotifications,
                    onChange: (value) => handleInputChange('notifications', 'prayerNotifications', value)
                },
                {
                    type: 'toggle',
                    label: 'Email Updates',
                    value: formData.notifications.emailUpdates,
                    onChange: (value) => handleInputChange('notifications', 'emailUpdates', value)
                }
            ]
        },
        {
            id: 'reading',
            title: 'Reading Preferences',
            icon: FiBook,
            fields: [
                {
                    type: 'toggle',
                    label: 'Show Transliteration',
                    value: formData.reading.showTransliteration,
                    onChange: (value) => handleInputChange('reading', 'showTransliteration', value)
                },
                {
                    type: 'toggle',
                    label: 'Auto Play Audio',
                    value: formData.reading.autoPlayAudio,
                    onChange: (value) => handleInputChange('reading', 'autoPlayAudio', value)
                }
            ]
        },
        {
            id: 'audio',
            title: 'Audio Settings',
            icon: FiVolume2,
            fields: [
                {
                    type: 'select',
                    label: 'Default Reciter',
                    value: formData.audio.defaultReciter,
                    options: Array.isArray(config?.reciters)
                        ? config.reciters.map(r => ({
                            value: r.id,
                            label: r.name
                        }))
                        : [],
                    onChange: (value) => handleInputChange('audio', 'defaultReciter', value)
                },
                {
                    type: 'range',
                    label: 'Playback Speed',
                    value: formData.audio.playbackSpeed,
                    min: 0.5,
                    max: 2.0,
                    step: 0.25,
                    onChange: (value) => handleInputChange('audio', 'playbackSpeed', parseFloat(value))
                }
            ]
        },
        {
            id: 'privacy',
            title: 'Privacy Settings',
            icon: FiLock,
            fields: [
                {
                    type: 'select',
                    label: 'Profile Visibility',
                    value: formData.privacy.profileVisibility,
                    options: [
                        { value: 'public', label: 'Public' },
                        { value: 'friends', label: 'Friends Only' },
                        { value: 'private', label: 'Private' }
                    ],
                    onChange: (value) => handleInputChange('privacy', 'profileVisibility', value)
                }
            ]
        }
    ];

    const renderField = (field) => {
        switch (field.type) {
            case 'text':
            case 'email':
                return (
                    <input
                        type={field.type}
                        value={field.value}
                        onChange={(e) => field.onChange?.(e.target.value)}
                        readOnly={field.readOnly}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${field.readOnly ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' : ''
                            }`}
                    />
                );

            case 'select':
                return (
                    <select
                        value={field.value}
                        onChange={(e) => field.onChange?.(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                        {field.options?.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );

            case 'radio':
                return (
                    <div className="space-y-2">
                        {field.options?.map(option => (
                            <label key={option.value} className="flex items-center space-x-3">
                                <input
                                    type="radio"
                                    name={field.label}
                                    value={option.value}
                                    checked={field.value === option.value}
                                    onChange={(e) => field.onChange?.(e.target.value)}
                                    className="text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-gray-700 dark:text-gray-300">{option.label}</span>
                            </label>
                        ))}
                    </div>
                );

            case 'toggle':
                return (
                    <label className="flex items-center space-x-3">
                        <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange?.(e.target.checked)}
                            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-gray-700 dark:text-gray-300">Enable</span>
                    </label>
                );

            case 'range':
                return (
                    <div className="space-y-2">
                        <input
                            type="range"
                            min={field.min}
                            max={field.max}
                            step={field.step}
                            value={field.value}
                            onChange={(e) => field.onChange?.(e.target.value)}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                            {field.value}x
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="space-y-8">
            {sections.map((section, index) => (
                <motion.div
                    key={section.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                >
                    <div className="flex items-center space-x-3 mb-6">
                        <SafeIcon icon={section.icon} className="w-5 h-5 text-emerald-600" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {section.title}
                        </h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {section.fields.map((field, fieldIndex) => (
                            <div key={fieldIndex}>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {field.label}
                                </label>
                                {renderField(field)}
                            </div>
                        ))}
                    </div>
                </motion.div>
            ))}

            {/* Save Button */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex justify-end"
            >
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSave}
                    className="flex items-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors"
                >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>Save Changes</span>
                </motion.button>
            </motion.div>
        </div>
    );
};

export default PersonalizationTab;